/* eslint-disable @typescript-eslint/explicit-module-boundary-types */
/* eslint-disable @typescript-eslint/no-explicit-any */
import { useI18n as vueUseI18n, createI18n } from 'vue-i18n';
import qs from 'qs';
import { getBrowserFullLocale, getBrowserLocale } from './libs/getBrowserLocale';

const ALL_LOCALES = ['en', 'en_GB', 'de', 'es', 'fr', 'it', 'nl', 'pt', 'ru'];

let SUPPORTED_LOCALES = ALL_LOCALES;

// https://medium.com/i18n-and-l10n-resources-for-developers/the-ultimate-guide-to-vue-localization-with-vue-i18n-bf98b1d40c65

function isLocaleSupported(locale: string | undefined): boolean {
  if (!locale) {
    return false;
  }

  return SUPPORTED_LOCALES.includes(locale);
}

function setSupportedLocales(locales: string[] | undefined) {
  if (!locales || locales.length === 0) {
    SUPPORTED_LOCALES = ALL_LOCALES;
    return;
  }

  const supportedLocales: string[] = [];
  for (let i = 0; i < locales.length; i += 1) {
    const loc = (locales[i] || '').trim().toLowerCase();

    if (ALL_LOCALES.indexOf(loc) >= 0) {
      supportedLocales.push(loc);
      if (loc === 'en') {
        supportedLocales.push('en_GB');
      }
    }
  }

  if (supportedLocales.length > 0) {
    SUPPORTED_LOCALES = supportedLocales;
  }
}
function getSupportedLocales() {
  return SUPPORTED_LOCALES;
}

function getAllLocales() {
  return ALL_LOCALES;
}

function getStartupLocale(): string {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const queryStringParams = qs.parse(window.location.search.substring(1)) as any;

  const qsLocale = queryStringParams.lg || queryStringParams.lang;

  if (qsLocale && isLocaleSupported(qsLocale)) {
    return qsLocale;
  }

  // eslint-disable-next-line no-underscore-dangle
  const canLG = (window as any)._lg;
  if (canLG && isLocaleSupported(canLG)) {
    return canLG;
  }

  let browserLocale = getBrowserFullLocale();

  if (browserLocale && isLocaleSupported(browserLocale)) {
    return browserLocale;
  }

  browserLocale = getBrowserLocale();

  if (browserLocale && isLocaleSupported(browserLocale)) {
    return browserLocale;
  }

  return process.env.VUE_APP_I18N_LOCALE || 'en';
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
function setI18nLanguage(i18n: any, locale: string) {
  if (i18n.mode === 'legacy') {
    // eslint-disable-next-line no-param-reassign
    i18n.global.locale = locale;
  } else {
    // eslint-disable-next-line no-param-reassign
    i18n.locale.value = locale;
  }
}

/*
// eslint-disable-next-line @typescript-eslint/no-explicit-any
function getI18nLanguage(i18n: any) {
  if (i18n.mode === 'legacy') {
    return i18n.global.locale;
  }
  return i18n.locale.value;
}
*/

/**
 * Modify the locale
 * @param locale
 * @returns  the loaded locale
 * https://vue-i18n.intlify.dev/guide/advanced/lazy.html
 */

function setLocale(i18nInstance: any, locale: string): Promise<string> {
  // If the language hasn't been loaded yet
  return import(/* webpackChunkName: "l-[request]" */ `./locales/${locale}.json`).then((messages) => {
    setI18nLanguage(i18nInstance, locale);
    i18nInstance.setLocaleMessage(locale, messages.default);

    return locale;
  });
}

// The I18n instance created by setupI18n()
let i18nInstance: any;

/**
 * Returns the real global composer (locale ref, t, setLocaleMessage, ...).
 * Since vue-i18n 9.2, useI18n() in legacy mode returns a wrapper composer with
 * its own message store, even with useScope: 'global'. The app loads locale
 * messages through this handle, so they must land on the global instance.
 * In legacy mode the global VueI18n exposes its composer as __composer.
 */
function useI18n(): any {
  if (i18nInstance) {
    // eslint-disable-next-line no-underscore-dangle
    return i18nInstance.global.__composer || i18nInstance.global;
  }
  return vueUseI18n({ useScope: 'global' });
}

/**
 * Init i18n and load the startup language
 */
function setupI18n(): any {
  const i18n = createI18n({
    legacy: true, // legacy for now, all compenent not using yet composition api
    allowComposition: true, // vue-i18n >= 9.2: needed for useI18n() calls while in legacy mode
    fallbackLocale: 'en',
    messages: {},
  });

  i18nInstance = i18n;
  return i18n;
}

/**
 * Exports
 */

export { getStartupLocale, setSupportedLocales, isLocaleSupported, setLocale, useI18n, setupI18n, getSupportedLocales, getAllLocales };

export default {};
